import type { DateRange } from '@/components/analytics';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/context/organization-context';
import { ConnectIntegrationsEmptyState } from '@/components/dashboard/empty-states';
import { useAnalyticsControllerGetRevenueAnalytics } from '@/apis/backend/generated/analytics/analytics';
import { MetricCard, PeriodSelector, TimeSeriesChart, useDefaultDateRange } from '@/components/analytics';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/analytics/revenue')({
  component: RevenueAnalyticsPage,
});

function RevenueAnalyticsPage() {
  const { t } = useTranslation('analytics');
  const { activeOrganization, activeClient } = useOrganization();
  const defaultRange = useDefaultDateRange(90);
  const [dateRange, setDateRange] = useState<DateRange>(defaultRange);

  const orgId = activeOrganization?.id;
  const clientId = activeClient?.id;

  const { data, isLoading } = useAnalyticsControllerGetRevenueAnalytics(
    {
      orgId: orgId!,
      clientId: clientId!,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      previousStartDate: dateRange.previousStartDate,
      previousEndDate: dateRange.previousEndDate,
    },
    { query: { enabled: !!orgId && !!clientId, staleTime: 60000 } }
  );

  if (!orgId || !clientId) return null;

  return (
    <Stack spacing={3}>
      <PeriodSelector value={dateRange} onChange={setDateRange} />

      {isLoading ? (
        <Stack direction="row" spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={120} sx={{ flex: 1, borderRadius: '12px' }} />
          ))}
        </Stack>
      ) : data && !data.available ? (
        <ConnectIntegrationsEmptyState
          title={t('revenue.empty.title', 'Connect Klaviyo to see revenue attribution')}
          description={t('revenue.empty.description', 'Revenue attribution requires an ESP that tracks placed-order events. Connect Klaviyo to see attributed revenue, campaign-vs-flow split, and first-time-vs-returning breakdowns.')}
        />
      ) : data ? (
        <>
          {/* Top KPIs */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <MetricCard title={t('revenue.metrics.attributedRevenue', 'Attributed Revenue')} metric={data.attributedRevenue} format="currency" />
            <MetricCard title={t('revenue.metrics.totalStoreRevenue', 'Total Store Revenue')} metric={data.totalStoreRevenue} format="currency" />
            <MetricCard title={t('revenue.metrics.percentOfTotal', 'Percent of Total')} metric={data.percentOfTotal} format="percent" />
          </Box>

          {/* Revenue by type */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <MetricCard title={t('revenue.metrics.campaignRevenue', 'Campaign Revenue')} metric={data.revenueByType.campaign} format="currency" />
            <MetricCard title={t('revenue.metrics.flowRevenue', 'Flow Revenue')} metric={data.revenueByType.flow} format="currency" />
          </Box>

          {/* Revenue by channel */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <MetricCard title={t('revenue.metrics.emailRevenue', 'Email Revenue')} metric={data.revenueByChannel.email} format="currency" />
            <MetricCard title={t('revenue.metrics.smsRevenue', 'SMS Revenue')} metric={data.revenueByChannel.sms} format="currency" />
          </Box>

          {/* First-time vs returning */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <MetricCard title={t('revenue.metrics.firstTimeBuyers', 'First-Time Buyers')} metric={data.firstTimeVsReturning.firstTime} format="currency" />
            <MetricCard title={t('revenue.metrics.returningCustomers', 'Returning Customers')} metric={data.firstTimeVsReturning.returning} format="currency" />
          </Box>

          {/* Revenue time series */}
          {data.timeSeries && data.timeSeries.length > 0 && (
            <TimeSeriesChart
              title={t('revenue.chart.title', 'Revenue Over Time')}
              data={data.timeSeries}
              series={[
                { dataKey: 'revenue', name: t('revenue.chart.seriesName', 'Revenue'), type: 'bar' },
              ]}
              formatTooltip={(value) => `$${value.toLocaleString()}`}
            />
          )}
        </>
      ) : (
        <Typography color="text.secondary">{t('revenue.noData', 'No revenue data available for this period.')}</Typography>
      )}
    </Stack>
  );
}
